import { BIG_ONE } from "@/lib/tool-kit/math/bigint";
import {
  addR,
  divR,
  fromBigInt,
  isIntegerR,
  isZeroR,
  mulR,
  type Rational,
  signR,
  subR,
} from "@/lib/tool-kit/math/rational";

/**
 * Arithmetic sequences, in exact rational arithmetic with whole-number positions.
 *
 * aₙ = a₁ + (n − 1)d and Sₙ = n(a₁ + aₙ) ÷ 2 = n(2a₁ + (n − 1)d) ÷ 2.
 */

const TWO = fromBigInt(BigInt(2));

export function nthTerm(a1: Rational, d: Rational, n: bigint): Rational {
  return addR(a1, mulR(fromBigInt(n - BIG_ONE), d));
}

/** The sum of the first n terms. */
export function sumOfTerms(a1: Rational, d: Rational, n: bigint): Rational {
  const last = nthTerm(a1, d, n);
  return divR(mulR(fromBigInt(n), addR(a1, last)), TWO);
}

export interface TwoTermFit {
  d: Rational;
  a1: Rational;
}

/** The sequence through aₚ = x and a_q = y (p ≠ q), or null when p = q. */
export function fitTwoTerms(
  p: bigint,
  x: Rational,
  q: bigint,
  y: Rational
): TwoTermFit | null {
  if (p === q) {
    return null;
  }
  const d = divR(subR(y, x), fromBigInt(q - p));
  const a1 = subR(x, mulR(fromBigInt(p - BIG_ONE), d));
  return { d, a1 };
}

export type PositionResult =
  | { kind: "term"; n: bigint }
  | { kind: "not-a-term" }
  | { kind: "every-term" };

/** Which position holds `target`, if any. */
export function positionOf(
  a1: Rational,
  d: Rational,
  target: Rational
): PositionResult {
  const gap = subR(target, a1);
  if (isZeroR(d)) {
    return isZeroR(gap) ? { kind: "every-term" } : { kind: "not-a-term" };
  }
  const steps = divR(gap, d);
  if (!isIntegerR(steps) || signR(steps) < 0) {
    return { kind: "not-a-term" };
  }
  return { kind: "term", n: steps.num + BIG_ONE };
}

/** The first `count` terms. */
export function firstTerms(
  a1: Rational,
  d: Rational,
  count: number
): Rational[] {
  const terms: Rational[] = [];
  let current = a1;
  for (let i = 0; i < count; i += 1) {
    terms.push(current);
    current = addR(current, d);
  }
  return terms;
}
